import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import SalaryForm from '../components/SalaryForm';
import { canEditSalaries } from '../utils/compensationPermissions';

const EditSalary = () => {
  const { hasPermission, loading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const methods = useForm();
  const { handleSubmit, reset, formState: { isSubmitting } } = methods;
  
  const [employees, setEmployees] = useState([]);
  const [banks, setBanks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [salaryLoaded, setSalaryLoaded] = useState(false);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [paymentMethodsLoaded, setPaymentMethodsLoaded] = useState(false);
  const [fetchedSalary, setFetchedSalary] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  
  // Only HR/admin (salaries:update_all) may edit a salary record. Every employee holds
  // salaries:update_own, so the generic hasUpdatePermission() would let them open this
  // form for their own record and change their own pay.
  const canEdit = canEditSalaries(hasPermission);

  useEffect(() => {
    // Wait for AuthContext to resolve - during initial load/refresh hasPermission
    // returns false transiently, which would prematurely redirect an authorized user.
    if (loading) return;
    if (!canEdit) {
      toastPermissionDenied('You do not have permission to edit salary records');
      navigate('/salaries');
    }
  }, [loading, canEdit, navigate]);
  
  useEffect(() => {
    // Only the latest run may populate the form: a stale response (StrictMode's
    // double run in dev, or an id change) would otherwise land after the user has
    // started typing and the reset effect below would discard their edits.
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [salaryRes, empRes, pmRes, bankRes] = await Promise.all([
          api.get(`/api/salaries/${id}`),
          api.get('/api/employees', { params: { limit: 10000 } }), // Fetch all employees for dropdown
          api.get('/api/payment-methods'),
          api.get('/api/banks')
        ]);
        if (cancelled) return;
        
        const salaryData = salaryRes.data.data;
        // Normalized response already has: currency, amount, payment_method_id, effective_date
        salaryData.employee_id = salaryData.employee_id ? String(salaryData.employee_id) : '';
        salaryData.payment_method_id = salaryData.payment_method_id ? String(salaryData.payment_method_id) : '';
        salaryData.bank_id = salaryData.bank_id ? String(salaryData.bank_id) : '';
        
        setFetchedSalary(salaryData);
        setEmployees(empRes.data.data || []);
        setPaymentMethods(pmRes.data.data);
        setBanks(bankRes.data.data);
        
        // Get history count
        if (salaryData.history && Array.isArray(salaryData.history)) {
          setHistoryCount(salaryData.history.length);
        } else if (salaryData.history) {
          try {
            const history = typeof salaryData.history === 'string' ? JSON.parse(salaryData.history) : salaryData.history;
            setHistoryCount(Array.isArray(history) ? history.length : 0);
          } catch (e) {
            setHistoryCount(0);
          }
        }
        
        setSalaryLoaded(true);
        setEmployeesLoaded(true);
        setPaymentMethodsLoaded(true);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load data');
      }
    };
    
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Sync form values after all data is loaded
  useEffect(() => {
    if (salaryLoaded && employeesLoaded && paymentMethodsLoaded && fetchedSalary) {
      reset(fetchedSalary);
    }
  }, [salaryLoaded, employeesLoaded, paymentMethodsLoaded, fetchedSalary, reset]);

  const onSubmit = async (data) => {
    try {
      await api.put(`/api/salaries/${id}`, data);
      toast.success('Salary record updated!');
      navigate('/salaries');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update salary record');
    }
  };

  if (!salaryLoaded || !employeesLoaded || !paymentMethodsLoaded) {
    return (
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
      <h2 className="text-2xl font-bold mb-6">Edit Salary Record</h2>
      {historyCount > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This change will be added to the salary history. There are currently {historyCount} previous change{historyCount !== 1 ? 's' : ''} in the history.
          </p>
        </div>
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <SalaryForm 
            employees={employees}
            paymentMethods={paymentMethods}
            banks={banks}
            mode="edit"
          />
          <button type="submit" disabled={isSubmitting} data-testid="salary-form-submit" className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors">
            {isSubmitting ? 'Updating...' : 'Update Salary Record'}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditSalary; 